/* ==========================================================================
   CADOO PERSONALIZED GIFTS - global helpers (loaded on every page)
   --------------------------------------------------------------------------
   1. ADD-BY-VARIANT GUARD
      Product cards rendered by snippets/cadoo-product-card.liquid already know
      whether a product needs customer input and show PERSONALIZE instead of
      Quick Add. Some surfaces only know a variant id (the Saved drawer/page
      "Move to bag", built from /products/<handle>.js which carries no
      metafields). For those, this asks /products/<handle>?view=pzcheck first:
      a product that needs a photo/name opens its product page instead of being
      added incomplete. Everything else is re-dispatched to the normal handler
      in assets/cadoo.js unchanged. Fails open (the bag's missing-photo gate
      still applies).

   2. PHOTO PREVIEW OVERLAY ("View photo")
      Shopify serves customer uploads (cdn.shopify.com/.../uploads/...) with
      "Content-Disposition: attachment", so opening the file URL directly makes
      the browser DOWNLOAD it - on iPhone this shows as a stalled tab or a
      download prompt - and the original can be many MB. Every theme-rendered
      link to an uploaded photo (cart page, gifting/delivery steps, cart
      drawer, product page) therefore opens this lightweight overlay instead:
        - loads Shopify's resized display copy (?width=...; verified to work
          for /uploads/ JPG and PNG, never enlarges) - the stored production
          file is never changed;
        - fits the whole image on screen (object-fit: contain);
        - Close button, Escape, backdrop click; focus returns to the link;
          the page, cart and scroll position are untouched;
        - loading feedback, and a recoverable error instead of an endless
          spinner (timeout). On failure it checks the original file: a missing
          file (404) is reported as such - it is never hidden and the cart is
          never changed.
      Shopify-owned checkout pages are outside the theme and keep their own
      "Your photo" link.
   ========================================================================== */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- 1 */
  var cache = {};
  function base(url) { return String(url || '').split('#')[0].split('?')[0]; }
  function check(url) {
    var u = base(url);
    if (!cache[u]) {
      /* No Accept: application/json header - with it Shopify answers with its
         own product JSON instead of the pzcheck template. */
      cache[u] = fetch(u + '?view=pzcheck', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (t) {
          var d = null;
          try { d = JSON.parse(t); } catch (e) { d = null; }
          return d && typeof d.requires === 'boolean' ? d : null;
        })
        .catch(function () { return null; })
        .then(function (d) { if (!d) delete cache[u]; return d; });
    }
    return cache[u];
  }
  window.addEventListener('click', function (e) {
    var b = e.target && e.target.closest && e.target.closest('[data-cd-quickadd]');
    if (!b || b.__cdPzOk) return;
    var url = b.getAttribute('data-cd-url');
    if (!url) return;
    /* Liquid-rendered cards are already gated server-side. */
    if (b.getAttribute('data-cd-source') !== 'saved' && b.closest('[data-cd-card]')) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (b.__cdPzChecking) return;
    b.__cdPzChecking = true;
    b.setAttribute('aria-busy', 'true');
    check(url).then(function (d) {
      b.__cdPzChecking = false;
      b.removeAttribute('aria-busy');
      if (d && d.requires) { window.location.assign(base(url)); return; }
      b.__cdPzOk = true;
      try { b.click(); } finally { b.__cdPzOk = false; }
    });
  }, true);

  /* ---------------------------------------------------------------- 2 */
  var UPLOAD = /^https:\/\/cdn\.shopify\.com\/.+\/uploads\/[^?#]+\.(jpe?g|png|gif|webp)(\?.*)?$/i;
  var V = {
    timeoutMs: 20000,
    T: {
      loading: 'Loading your photo…',
      error: 'We couldn’t load the preview. Try again.',
      missing: 'We couldn’t find this photo file. Use “Edit personalization” in your bag to add your photo again.',
      retry: 'Try again',
      close: 'Close'
    }
  };
  var dlg, img, loadingEl, errorEl, errorText, retryBtn, closeBtn, opener, currentUrl, timer, attempt = 0, prevOverflow = '';

  function css() {
    if (document.getElementById('cdv-style')) return;
    var s = document.createElement('style');
    s.id = 'cdv-style';
    s.textContent = [
      '.cdv{position:fixed;inset:0;width:100%;height:100%;max-width:none;max-height:none;margin:0;padding:0;border:0;background:transparent;color:#14213D;overflow:hidden}',
      '.cdv::backdrop{background:rgba(20,33,61,.82)}',
      '.cdv[open]{display:flex;align-items:center;justify-content:center}',
      '.cdv__bg{position:absolute;inset:0}',
      '.cdv__frame{position:relative;display:flex;flex-direction:column;align-items:center;gap:12px;width:100%;height:100%;padding:max(14px,env(safe-area-inset-top)) 14px max(16px,env(safe-area-inset-bottom));box-sizing:border-box;pointer-events:none}',
      '.cdv__bar{width:100%;max-width:1100px;display:flex;justify-content:flex-end;pointer-events:none}',
      '.cdv__close{display:inline-flex;align-items:center;gap:8px;min-height:44px;padding:0 18px;border:0;border-radius:999px;background:#FAF7F2;color:#14213D;font:700 15px/1 var(--cd-sans,Karla,sans-serif);cursor:pointer;box-shadow:0 2px 12px rgba(0,0,0,.25);pointer-events:auto}',
      '.cdv__close:focus-visible,.cdv__retry:focus-visible{outline:3px solid #F47C5C;outline-offset:2px}',
      '.cdv__close svg{width:16px;height:16px}',
      '.cdv__stage{position:relative;flex:1;min-height:0;width:100%;max-width:1100px;display:flex;align-items:center;justify-content:center}',
      '.cdv__img{display:block;max-width:100%;max-height:100%;width:auto;height:auto;object-fit:contain;border-radius:8px;background:#fff;box-shadow:0 8px 40px rgba(0,0,0,.35);pointer-events:auto}',
      '.cdv__img[hidden]{display:none}',
      '.cdv__msg{display:flex;flex-direction:column;align-items:center;gap:14px;padding:22px 24px;max-width:320px;border-radius:16px;background:#FAF7F2;text-align:center;font:500 15px/1.45 var(--cd-sans,Karla,sans-serif);pointer-events:auto}',
      '.cdv__msg[hidden]{display:none}',
      '.cdv__spin{width:34px;height:34px;border-radius:50%;border:3px solid rgba(20,33,61,.15);border-top-color:#F47C5C;animation:cdv-spin .9s linear infinite}',
      '@keyframes cdv-spin{to{transform:rotate(360deg)}}',
      '@media (prefers-reduced-motion:reduce){.cdv__spin{animation:none;border-top-color:#14213D}}',
      '.cdv__err{color:#8C1D18;font-weight:600;margin:0}',
      '.cdv__retry{min-height:44px;padding:0 22px;border:0;border-radius:999px;background:#14213D;color:#fff;font:700 15px/1 var(--cd-sans,Karla,sans-serif);cursor:pointer}'
    ].join('\n');
    document.head.appendChild(s);
  }

  function build() {
    if (dlg) return;
    css();
    dlg = document.createElement('dialog');
    dlg.className = 'cdv';
    dlg.setAttribute('aria-label', 'Photo preview');
    dlg.innerHTML =
      '<div class="cdv__bg" data-cdv-close></div>' +
      '<div class="cdv__frame">' +
        '<div class="cdv__bar"><button type="button" class="cdv__close" data-cdv-close>' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg><span>' + V.T.close + '</span></button></div>' +
        '<div class="cdv__stage">' +
          '<div class="cdv__msg" data-cdv-loading role="status"><span class="cdv__spin" aria-hidden="true"></span><span>' + V.T.loading + '</span></div>' +
          '<div class="cdv__msg" data-cdv-error role="alert" hidden><p class="cdv__err" data-cdv-error-text></p><button type="button" class="cdv__retry" data-cdv-retry>' + V.T.retry + '</button></div>' +
          '<img class="cdv__img" alt="Your uploaded photo" hidden>' +
        '</div>' +
      '</div>';
    img = dlg.querySelector('.cdv__img');
    loadingEl = dlg.querySelector('[data-cdv-loading]');
    errorEl = dlg.querySelector('[data-cdv-error]');
    errorText = dlg.querySelector('[data-cdv-error-text]');
    retryBtn = dlg.querySelector('[data-cdv-retry]');
    closeBtn = dlg.querySelector('.cdv__close');

    /* Nothing inside the overlay may reach page-level handlers (drawers close
       on outside clicks / Escape). */
    ['click', 'mousedown', 'pointerdown', 'touchstart', 'keydown'].forEach(function (t) {
      dlg.addEventListener(t, function (e) { e.stopPropagation(); });
    });
    dlg.addEventListener('click', function (e) {
      if (e.target.closest('[data-cdv-close]') || e.target === dlg) { close(); return; }
      if (e.target.closest('[data-cdv-retry]')) { attempt++; load(); }
    });
    dlg.addEventListener('cancel', function (e) { e.preventDefault(); close(); });   /* Escape */
    dlg.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key === 'Tab') {   /* keep focus on the overlay's own controls */
        var f = [closeBtn].concat(errorEl.hidden ? [] : [retryBtn]);
        var i = f.indexOf(document.activeElement);
        e.preventDefault();
        f[(i + (e.shiftKey ? -1 : 1) + f.length) % f.length].focus();
      }
    });
    img.addEventListener('load', function () {
      if (!dlg.open) return;
      clearTimeout(timer);
      loadingEl.hidden = true; errorEl.hidden = true; img.hidden = false;
      dlg.setAttribute('data-state', 'loaded');
    });
    img.addEventListener('error', function () { if (dlg.open && img.getAttribute('src')) fail(); });
    document.body.appendChild(dlg);
  }

  function displayUrl(url) {
    var w = Math.max(window.innerWidth || 0, window.innerHeight || 0) * Math.min(window.devicePixelRatio || 1, 2);
    w = Math.min(2000, Math.max(800, Math.ceil(w / 200) * 200));
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w + (attempt ? '&cdv=' + attempt : '');
  }

  function load() {
    clearTimeout(timer);
    dlg.setAttribute('data-state', 'loading');
    img.hidden = true; errorEl.hidden = true; loadingEl.hidden = false;
    img.removeAttribute('src');
    timer = setTimeout(function () { if (dlg.open && dlg.getAttribute('data-state') === 'loading') fail(); }, V.timeoutMs);
    img.src = displayUrl(currentUrl);
  }

  function fail() {
    clearTimeout(timer);
    img.removeAttribute('src');
    img.hidden = true; loadingEl.hidden = true;
    errorText.textContent = V.T.error;
    errorEl.hidden = false;
    dlg.setAttribute('data-state', 'error');
    try { retryBtn.focus(); } catch (e) {}
    /* Is the saved file itself reachable? Distinguish "file missing" from a
       slow or failed preview. Read-only: never changes the cart. */
    var url = currentUrl;
    fetch(url, { method: 'HEAD', cache: 'no-store' }).then(function (r) {
      if (dlg.open && url === currentUrl && (r.status === 404 || r.status === 403 || r.status === 410)) {
        errorText.textContent = V.T.missing;
        dlg.setAttribute('data-state', 'missing');
      }
    }, function () {});
  }

  function open(url, from) {
    build();
    opener = from || document.activeElement;
    currentUrl = url;
    attempt = 0;
    prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';   /* lock scroll without moving the page */
    try { dlg.showModal(); } catch (e) { dlg.setAttribute('open', ''); }
    try { closeBtn.focus({ preventScroll: true }); } catch (e) {}
    load();
  }

  function close() {
    if (!dlg || !dlg.open) return;
    clearTimeout(timer);
    img.removeAttribute('src');
    try { dlg.close(); } catch (e) { dlg.removeAttribute('open'); }
    dlg.removeAttribute('data-state');
    document.documentElement.style.overflow = prevOverflow;
    if (opener && opener.focus) { try { opener.focus({ preventScroll: true }); } catch (e) {} }
  }

  window.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target && e.target.closest && e.target.closest('a[href]');
    if (!a) return;
    var href = a.getAttribute('href') || '';
    if (!a.hasAttribute('data-cd-photo-view') && !UPLOAD.test(href)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    open(href.split('?')[0], a);
  }, true);

  /* Escape closes only the preview, never an open cart drawer behind it. */
  window.addEventListener('keydown', function (e) {
    if (dlg && dlg.open && (e.key === 'Escape' || e.key === 'Esc')) {
      e.preventDefault(); e.stopImmediatePropagation(); close();
    }
  }, true);

  window.CadooPhotoViewer = { open: open, close: close, settings: V };

  /* ---------------------------------------------------------------- 3 */
  /* CART DRAWER CHECKOUT. The drawer (assets/cadoo.js) lists bag lines but
     does not know which products need personalization. Before its Checkout
     continues, each line is checked against ?view=pzcheck (which reports the
     product's allowed methods): a line whose chosen method is incomplete - no
     Shopify-hosted photo, no name / initials, no letter or name, a font or
     colour not chosen, a method the product no longer offers, or no
     personalization at all - stops here with a visible notice and an action
     that edits that exact line (?cd_edit=<line key>: quantity and other
     options kept, no duplicate). Same rule as the bag's Liquid gate
     (snippets/cadoo-personalization-summary.liquid). Fails open - the bag
     page and the gifting/delivery steps keep their own block. */
  var HOSTED = /^https:\/\/cdn\.shopify\.com\/.+\/uploads\//i;
  function blankV(v) { return v == null || String(v).trim() === ''; }
  /* null = complete; otherwise 'photo', 'text' (what is missing), 'choice'
     (a font / colour not chosen) or 'method' (method not offered for this
     product). Current labels need every choice of their method; legacy
     labels (Name / Initials / Photo, from the old form) are checked as
     before, text or photo only, so older complete lines stay valid. A line's
     method must be one the product currently offers. */
  var KIND = { 'Upload a photo or logo': 'photo_upload', 'Photo': 'photo_upload', 'Monogram + name': 'letter_name', 'Letter + name': 'letter_name', 'Name or initials': 'name_initials', 'Name': 'name_initials', 'Initials': 'name_initials' };
  function incomplete(p, d) {
    if (!d || !d.requires) return null;
    p = p || {};
    var kind = p.Personalization || '';
    var photoOk = HOSTED.test(p['Your photo'] || '');
    var id = KIND[kind];
    if (id && d.methods && d.methods.length && d.methods.indexOf(id) < 0) return 'method';
    if (id === 'photo_upload') return photoOk ? null : 'photo';
    if (kind === 'Monogram + name' || kind === 'Letter + name') {
      if (blankV(p.Letter) || blankV(p.Name)) return 'text';
      return (blankV(p['Letter colour']) || blankV(p['Name colour']) || blankV(p['Name font'])) ? 'choice' : null;
    }
    if (kind === 'Name or initials') {
      if (blankV(p['Name or initials'])) return 'text';
      return (blankV(p.Font) || blankV(p['Print colour'])) ? 'choice' : null;
    }
    if (kind === 'Name') return blankV(p.Name) ? 'text' : null;
    if (kind === 'Initials') return blankV(p.Initials) ? 'text' : null;
    if (photoOk) return null;
    var only = d.methods && d.methods.length === 1 && d.methods[0] === 'photo_upload';
    return (only || d.mode === 'photo_upload') ? 'photo' : 'text';
  }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return '&#' + c.charCodeAt(0) + ';'; }); }
  function missingLines() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) {
        if (!c || !c.items) return [];
        return Promise.all(c.items.map(function (i) {
          return check(i.url).then(function (d) {
            var what = incomplete(i.properties, d);
            if (!what) return null;
            i.__cdMissing = what;
            return i;
          });
        })).then(function (a) { return a.filter(Boolean); });
      })
      .catch(function () { return []; });
  }
  function drawerNotice(f, miss, anchor) {
    var n = document.getElementById('cd-pz-drawer-missing');
    if (!n) {
      n = document.createElement('div');
      n.id = 'cd-pz-drawer-missing';
      n.setAttribute('role', 'alert');
      n.style.cssText = 'margin:0 0 12px;padding:10px 12px;border-radius:10px;background:#FDF4F3;border:1px solid #E8B4AE;color:#8C1D18;font-size:14px;line-height:1.45;text-align:left;';
    }
    var first = miss[0];
    var photo = miss.every(function (i) { return i.__cdMissing === 'photo'; });
    var what = photo ? 'your photo or logo' : 'its personalization';
    var fix = miss.every(function (i) { return i.__cdMissing === 'choice' || i.__cdMissing === 'method'; });
    if (fix) what = 'its personalization updated';
    n.innerHTML = '<strong style="display:block;font-weight:700">' + (photo ? 'Your photo is missing' : fix ? 'Personalization needs updating' : 'Personalization is missing') + '</strong>' +
      '<span style="display:block">' + (miss.length > 1
        ? miss.length + ' items in your bag need their personalization before checkout.'
        : '“' + esc(first.product_title) + '” needs ' + what + ' before checkout.') + '</span>' +
      '<a style="display:inline-flex;align-items:center;min-height:40px;margin-top:8px;padding:0 16px;border-radius:999px;background:#14213D;color:#fff;font-weight:700;text-decoration:none">' + (photo ? 'Add your photo' : 'Complete it') + '</a>';
    n.querySelector('a').setAttribute('href', base(first.url) + '?cd_edit=' + encodeURIComponent(first.key));
    if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(n, anchor);
    else f.insertBefore(n, f.firstChild);
    try { n.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (e) {}
    try { n.querySelector('a').focus({ preventScroll: true }); } catch (e) {}
  }
  window.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f || !f.closest || !f.closest('#cd-cart') || f.__cdPzOk) return;
    var act = f.getAttribute('action') || '';
    if (!/^\/cart(\?|$)|\/checkout/.test(act)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (f.__cdPzChecking) return;
    var sub = e.submitter && e.submitter.form === f ? e.submitter : null;
    f.__cdPzChecking = true;
    if (sub) sub.setAttribute('aria-busy', 'true');
    missingLines().then(function (miss) {
      f.__cdPzChecking = false;
      if (sub) sub.removeAttribute('aria-busy');
      if (miss.length) { drawerNotice(f, miss, sub); return; }
      var old = document.getElementById('cd-pz-drawer-missing');
      if (old) old.parentNode.removeChild(old);
      f.__cdPzOk = true;
      try { if (f.requestSubmit) f.requestSubmit(sub || undefined); else f.submit(); } finally { f.__cdPzOk = false; }
    });
  }, true);

  /* ---------------------------------------------------------------- 4 */
  /* PRODUCT PAGE SAFETY NET. If assets/cadoo-personalize.js ever fails to
     load, cadoo.js would add a personalized product as a plain line with no
     personalization. Every add from a product page that has personalization
     fields ([data-cd-pz]) is blocked until the controller is present. */
  function pzBlocked(t) {
    if (window.CadooPersonalize) return false;
    var root = t && t.closest && t.closest('[data-cd-pdp]');
    if (!root || !root.querySelector('[data-cd-pz]')) return false;
    var e = root.querySelector('[data-cd-pz-err="general"]');
    if (e) { e.textContent = 'The personalization form didn’t load. Please refresh the page and try again.'; e.hidden = false; }
    return true;
  }
  window.addEventListener('submit', function (ev) { if (pzBlocked(ev.target)) { ev.preventDefault(); ev.stopImmediatePropagation(); } }, true);
  window.addEventListener('click', function (ev) {
    var b = ev.target && ev.target.closest && ev.target.closest('[data-cd-submit],[data-cd-sticky-btn]');
    if (b && pzBlocked(b)) { ev.preventDefault(); ev.stopImmediatePropagation(); }
  }, true);

  /* ---------------------------------------------------------------- 5 */
  /* CART DRAWER PERSONALIZATION SUMMARY. assets/cadoo.js (pzLineHtml) draws
     each personalized line with its original key list. This upgrades every
     such block to the full list - the same keys, order and rules as
     snippets/cadoo-personalization-summary.liquid - from the cart state
     cadoo.js already holds (no extra request). "Personalization" is shown
     only for the current method labels, so legacy lines read as before. The
     photo row and the "Edit personalization" link are kept as drawn. */
  var PZ_ORDER = ['Personalization', 'Name or initials', 'Letter', 'Letter colour', 'Name', 'Name colour', 'Name font', 'Initials', 'Method', 'Font', 'Print colour', 'Personalization notes', 'Instructions'];
  var PZ_METHODS = ['Name or initials', 'Monogram + name', 'Letter + name', 'Upload a photo or logo'];
  var pzQueued = false;
  function pzUpgrade() {
    pzQueued = false;
    var st = window.Cadoo && window.Cadoo.getCartState ? window.Cadoo.getCartState() : null;
    var items = st && st.items;
    if (!items) return;
    Array.prototype.forEach.call(document.querySelectorAll('.cd-line[data-key] .cd-line__pz:not([data-cd-pz-v2])'), function (blk) {
      var line = blk.closest('.cd-line[data-key]');
      var key = line && line.getAttribute('data-key');
      var it = null;
      for (var i = 0; i < items.length; i++) if (items[i].key === key) { it = items[i]; break; }
      if (!it) return;
      var p = it.properties || {};
      var html = '';
      PZ_ORDER.forEach(function (k) {
        var v = p[k];
        if (k === 'Personalization' && PZ_METHODS.indexOf(v) < 0) return;
        if (v == null || String(v).trim() === '') return;
        html += '<span class="cd-line__pzrow"><span class="cd-line__pzk">' + esc(k) + ':</span> <span dir="auto">' + esc(v) + '</span></span>';
      });
      var keep = [blk.querySelector('.cd-line__pzphoto'), blk.querySelector('.cd-line__pzedit')].filter(Boolean);
      blk.innerHTML = html;
      keep.forEach(function (n) { blk.appendChild(n); });
      blk.setAttribute('data-cd-pz-v2', '');
    });
  }
  function pzQueue() { if (!pzQueued) { pzQueued = true; (window.queueMicrotask || setTimeout)(pzUpgrade); } }
  if (window.MutationObserver) {
    new MutationObserver(pzQueue).observe(document.documentElement, { childList: true, subtree: true });
  }
  pzQueue();
})();
